import { compareDesc } from 'date-fns'
import type {
  ApprovalResponse,
  Assignment,
  NonPlaceholderStageTypes,
  Stage,
  StageTypes,
  StatusTypes,
  TaskResponse,
  Workflow,
  WorkflowStatus,
} from '../../types'

export type StageResource = {
  id: string
  attributes: {
    due_date: string | null
    metadata?: { title?: string } | null
    position: number
    stage_type: StageTypes
    status: Stage['status']
  }
  relationships: { assignments: { data: Array<{ id: string }> } }
}

export type AssignmentResource = {
  id: string
  attributes: {
    assignment_type: NonPlaceholderStageTypes
    created_at: string
    metadata?: { title?: string } | null
    position: number
  }
  relationships: {
    assigned_user?: { data: { id: string } | null }
    responses: { data: Array<{ id: string }> }
    stage: { data: { id: string } }
  }
}

export type ResponseResource = {
  id: string
  attributes: { created_at: string; details: unknown }
  relationships: { created_by_user: { data: { id: string } } }
}

export type WorkflowResource = {
  id: string
  attributes: { is_approved: boolean; status: StatusTypes }
  meta?: { completed_stages: number; latest_active_stage?: unknown }
}

type ApprovalMetadata = {
  title?: string
  completion_rules: 'one' | 'all'
} | null

type ApprovalDetails = {
  approved: 'approved' | 'declined'
  reasoning?: string | null
}

type TaskDetails = {
  complete: boolean
}

export type LatestStageApproval = {
  assignment_counts: {
    all: { complete: number; incomplete: number }
    currentUser: { complete: number; incomplete: number }
    declined: number
  }
  completion_rules: 'one' | 'all'
  resource_identifier: { data: { id: string; type: string } }
  stage_type: 'approval'
  title: string
}

/**
 * `meta.latest_active_stage` is generated per version. Both versions carry the
 * fields below, and `canRewindStage` reads them through the
 * `as unknown as LatestStageApproval` cast the v2 hooks already use.
 */
type LatestStage = {
  assignment_counts: {
    all: { complete: number; incomplete: number }
    current_user: { complete: number; incomplete: number }
    declined: number
  }
  resource_identifier: { data: { id: string } }
  stage_type: NonPlaceholderStageTypes
  title?: string | null
}

const latestStageOf = (workflow: WorkflowResource) =>
  workflow.meta?.latest_active_stage as LatestStage | undefined

export const canRewindStage = (
  latestStage: LatestStageApproval,
  isApproved: boolean
) => {
  const declined =
    !isApproved && Boolean(latestStage.assignment_counts.declined)
  const partiallyApproved =
    latestStage.completion_rules === 'all' &&
    latestStage.assignment_counts.all.complete &&
    latestStage.assignment_counts.all.incomplete

  if (declined || partiallyApproved) return true
  return false
}

const selectStage = (stage: StageResource, i: number): Stage => {
  const metadata = stage.attributes.metadata as ApprovalMetadata
  const circuitBreakerAssignmentId =
    i === 0 && stage.attributes.stage_type === 'circuit_breaker'
      ? stage.relationships.assignments.data[0]?.id
      : undefined

  return {
    id: stage.id,
    dueDate: stage.attributes.due_date,
    type: stage.attributes.stage_type,
    title: stage.attributes.metadata?.title,
    position: stage.attributes.position,
    status: stage.attributes.status,
    approvalType: metadata?.completion_rules ?? undefined,
    circuitBreakerAssignmentId,
  }
}

export const buildWorkflow = ({
  workflow,
  stages,
}: {
  workflow: WorkflowResource | undefined
  stages: Array<StageResource>
}): Workflow | null => {
  if (!workflow) return null
  const latestStage = latestStageOf(workflow)

  return {
    id: workflow.id,
    activeStageId: latestStage?.resource_identifier.data.id,
    approved: workflow.attributes.is_approved,
    canRewind: latestStage
      ? canRewindStage(
          latestStage as unknown as LatestStageApproval,
          workflow.attributes.is_approved
        )
      : false,
    status: workflow.attributes.status,
    stages: [...stages]
      .sort((a, b) => a.attributes.position - b.attributes.position)
      .map(selectStage),
  }
}

const selectDeclinedDetails = ({
  stageId,
  assignments,
  responses,
}: {
  stageId: string
  assignments: Array<AssignmentResource>
  responses: Array<ResponseResource>
}) => {
  const responseIds = assignments
    .filter((assignment) => assignment.relationships.stage.data.id === stageId)
    .flatMap((assignment) =>
      assignment.relationships.responses.data.map((response) => response.id)
    )
  const declinedResponses = responses.filter((response) => {
    if (!responseIds.includes(response.id)) return false
    if (!response.attributes.details) return false
    const details = response.attributes.details as ApprovalDetails
    return details.approved === 'declined' && details.reasoning
  })

  const latestDeclinedResponse = [...declinedResponses].sort((a, b) =>
    compareDesc(
      new Date(a.attributes.created_at),
      new Date(b.attributes.created_at)
    )
  )[0]

  if (!latestDeclinedResponse) return undefined
  const latestDetails = latestDeclinedResponse.attributes
    .details as ApprovalDetails

  return {
    reasoning: latestDetails.reasoning as string, // if declined reasoning must be present
    userId: latestDeclinedResponse.relationships.created_by_user.data.id,
  }
}

export const buildStatus = ({
  workflow,
  assignments,
  responses,
}: {
  workflow: WorkflowResource | undefined
  assignments: Array<AssignmentResource>
  responses: Array<ResponseResource>
}): WorkflowStatus | null => {
  if (!workflow) return null

  const latestStage = latestStageOf(workflow)
  let canRewind = false
  if (latestStage && latestStage.stage_type === 'approval') {
    canRewind = canRewindStage(
      latestStage as unknown as LatestStageApproval,
      workflow.attributes.is_approved
    )
  }

  let declined = undefined
  if (latestStage?.assignment_counts.declined) {
    declined = selectDeclinedDetails({
      stageId: latestStage.resource_identifier.data.id,
      assignments,
      responses,
    })
  }

  return {
    id: workflow.id,
    state: !workflow.meta?.completed_stages
      ? 'inactive'
      : workflow.attributes.status,
    approved: workflow.attributes.is_approved,
    latestStageCanRewind: canRewind,
    activeStage: latestStage
      ? {
          id: latestStage.resource_identifier.data.id,
          title: latestStage.title ?? '',
          stageType: latestStage.stage_type,
          declined,
          assignmentCountForUser:
            latestStage.assignment_counts.current_user.incomplete,
          assignmentCount: latestStage.assignment_counts.all.incomplete,
        }
      : null,
  }
}

export const buildAssignments = (
  assignments: Array<AssignmentResource>
): Array<Assignment> =>
  [...assignments]
    .sort((a, b) => a.attributes.position - b.attributes.position)
    .map((assignment) => ({
      id:
        assignment.attributes.created_at === 'unsaved'
          ? 'unsaved'
          : assignment.id,
      type: assignment.attributes.assignment_type,
      title: assignment.attributes.metadata?.title,
      assignedUserId: assignment.relationships.assigned_user?.data?.id,
      responseCount: assignment.relationships.responses.data.length,
    }))

export const buildResponses = (
  responses: Array<ResponseResource>,
  stageType: StageTypes
): Array<TaskResponse | ApprovalResponse> => {
  const sorted = [...responses].sort((a, b) =>
    compareDesc(
      new Date(a.attributes.created_at),
      new Date(b.attributes.created_at)
    )
  )

  if (stageType === 'approval') {
    return sorted.map((response): ApprovalResponse => {
      const details = response.attributes.details as ApprovalDetails
      return {
        id: response.id,
        approved: details?.approved === 'approved',
        createdAt: new Date(response.attributes.created_at),
        createdByUserUuid: response.relationships.created_by_user.data.id,
        reasoning: details?.reasoning ?? undefined,
      }
    })
  }

  return sorted.map((response): TaskResponse => {
    const details = response.attributes.details as TaskDetails
    return {
      id: response.id,
      complete: details?.complete,
      createdAt: new Date(response.attributes.created_at),
      createdByUserUuid: response.relationships.created_by_user.data.id,
    }
  })
}
